/* eslint-disable prettier/prettier */
import * as React from 'react';
import { FieldInputType } from '../utils/fieldInputType';
import { TemplateScoreMap, TemplateScoreContextType, TemplateSection, SectionField } from '../typings';

export const TemplateScoreContext = React.createContext<TemplateScoreContextType>({
  templateScoreMap: {},
  updateTemplateScoreMap: () => {}
});

interface TemplateScoreProviderProps {
  children: React.ReactNode;
  templateSections: TemplateSection[];
}

const getDefaultScore = (field: SectionField): number => {
  if (field.fieldInputType === FieldInputType.Checkbox && field.extra?.isDefault) return field.extra.score ?? 0;

  if (field.selectOptions) {
    return field.selectOptions.reduce((total, option) => option.isDefault ? total + (option.score ?? 0) : total, 0);
  }
  return 0;
};

const TemplateScoreProvider: React.FC<TemplateScoreProviderProps> = ({ children, templateSections }) => {
  const [templateScoreMap, setTemplateScoreMap] = React.useState<TemplateScoreMap>(() => {
    if (!templateSections) return {};

    return templateSections.reduce((scoreMap, section) => {
      const sectionScores = section.sectionFields.reduce((fieldScores, field) => {
        if (!field.extra?.usedForScore) return fieldScores;

        const defaultScore = getDefaultScore(field);
        return defaultScore ? { ...fieldScores, [field.rank]: defaultScore } : fieldScores;
      }, {});

      return Object.keys(sectionScores).length ?
        { ...scoreMap, [section.rank]: sectionScores } :
        scoreMap;
    }, {});
  });

  const updateTemplateScoreMap = (
    sectionRank: TemplateSection['rank'],
    rank: SectionField['rank'],
    score: number,
    usedForScore: boolean
  ) => {
    if (usedForScore) {
      const sectionScoreMap = templateScoreMap[sectionRank] ?? {};
      sectionScoreMap[rank] = score;
      setTemplateScoreMap({ ...templateScoreMap, [sectionRank]: sectionScoreMap });
    }
  };

  const scoreContext = { templateScoreMap, updateTemplateScoreMap };
  return <TemplateScoreContext.Provider value={scoreContext}>{children}</TemplateScoreContext.Provider>;
};

export default TemplateScoreProvider;
